import { useEffect, useState } from "react";
import { Plus, Trash2, Save, GripVertical, X, Inbox, Download, Mail, RefreshCw, ChevronDown, Loader2, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/RichText";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { ShareToFacebook } from "@/components/admin/ShareToFacebook";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

const DEFAULT_FORM = { title: "Sign Up", intro: "", submit_label: "Submit sign-up", show_type: true, show_team_name: true, show_name: true, show_fargo: true, show_phone: true, show_email: true, show_notes: false, custom_fields: [] };

const STD_FIELDS = [
  ["show_type", "Sign-up type (Singles / Team)"],
  ["show_team_name", "Team name (shown when Team selected)"],
  ["show_name", "Name"],
  ["show_fargo", "Fargo rating"],
  ["show_phone", "Contact #"],
  ["show_email", "Email"],
  ["show_notes", "Notes / message"],
];

function FormEditor({ p, i, upd, updForm }) {
  const f = { ...DEFAULT_FORM, ...(p.form || {}) };
  const fields = f.custom_fields || [];
  const setCustom = (next) => updForm(i, "custom_fields", next);
  const addCustom = () => setCustom([...fields, { id: crypto.randomUUID(), label: "", type: "text", options: [], enabled: true, required: false }]);
  const updCustom = (idx, k, v) => setCustom(fields.map((c, ci) => (ci === idx ? { ...c, [k]: v } : c)));
  const rmCustom = (idx) => setCustom(fields.filter((_, ci) => ci !== idx));

  return (
    <div className="space-y-3" data-testid={`promo-form-editor-${i}`}>
      <input className={input} placeholder="Button label (e.g. Sign me up)" value={p.cta_label} onChange={(e) => upd(i, "cta_label", e.target.value)} data-testid={`promo-cta-label-${i}`} />
      <div className="grid gap-3 sm:grid-cols-2">
        <input className={input} placeholder="Form title (e.g. New Session Sign Up)" value={f.title} onChange={(e) => updForm(i, "title", e.target.value)} data-testid={`promo-form-title-${i}`} />
        <input className={input} placeholder="Submit button label" value={f.submit_label} onChange={(e) => updForm(i, "submit_label", e.target.value)} data-testid={`promo-form-submit-${i}`} />
      </div>
      <textarea className={input} rows={2} placeholder="Form intro / instructions (optional)" value={f.intro} onChange={(e) => updForm(i, "intro", e.target.value)} data-testid={`promo-form-intro-${i}`} />

      <div>
        <p className="text-xs font-semibold text-[#555555] mb-1.5">Standard fields (toggle on/off)</p>
        <div className="grid sm:grid-cols-2 gap-1.5">
          {STD_FIELDS.map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-2 text-sm bg-white border border-[#E5E7EB] rounded-md px-2.5 py-1.5 cursor-pointer">
              <input type="checkbox" checked={f[key] !== false} onChange={(e) => updForm(i, key, e.target.checked)} data-testid={`promo-field-${key}-${i}`} className="h-3.5 w-3.5 accent-[#1E63C4]" />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-[#555555] mb-1.5">Extra custom fields</p>
        <div className="space-y-2">
          {fields.map((c, ci) => (
            <div key={c.id || ci} className="rounded-md border border-[#E5E7EB] bg-white p-2.5 space-y-2" data-testid={`promo-custom-${i}-${ci}`}>
              <div className="flex items-center gap-2">
                <input className={input} placeholder="Field label (e.g. Preferred division)" value={c.label} onChange={(e) => updCustom(ci, "label", e.target.value)} data-testid={`promo-custom-label-${i}-${ci}`} />
                <select className="rounded-md border border-[#E5E7EB] px-2 py-2 text-sm shrink-0" value={c.type} onChange={(e) => updCustom(ci, "type", e.target.value)} data-testid={`promo-custom-type-${i}-${ci}`}>
                  <option value="text">Text</option>
                  <option value="select">Dropdown</option>
                </select>
                <button type="button" onClick={() => rmCustom(ci)} className="text-red-500 shrink-0 p-1" aria-label="Remove field"><X className="h-4 w-4" /></button>
              </div>
              {c.type === "select" && (
                <textarea className={input} rows={2} placeholder="Dropdown options — one per line" value={(c.options || []).join("\n")} onChange={(e) => updCustom(ci, "options", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} data-testid={`promo-custom-options-${i}-${ci}`} />
              )}
              <div className="flex items-center gap-4">
                <label className="inline-flex items-center gap-1.5 text-xs text-[#555]"><input type="checkbox" checked={c.enabled !== false} onChange={(e) => updCustom(ci, "enabled", e.target.checked)} className="h-3.5 w-3.5 accent-[#1E63C4]" /> Enabled</label>
                <label className="inline-flex items-center gap-1.5 text-xs text-[#555]"><input type="checkbox" checked={!!c.required} onChange={(e) => updCustom(ci, "required", e.target.checked)} className="h-3.5 w-3.5 accent-[#1E63C4]" /> Required</label>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addCustom} data-testid={`promo-add-custom-${i}`} className="mt-2 inline-flex items-center gap-1 text-sm text-[#1E63C4] font-medium"><Plus className="h-4 w-4" /> Add custom field</button>
      </div>
    </div>
  );
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

function PromoSignups() {
  const [signups, setSignups] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/promo-signups").then((r) => setSignups(r.data.signups || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this sign-up?")) return;
    try { await api.delete(`/admin/promo-signups/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const exportCsv = () => {
    const rows = [["Promo", "Type", "Team", "Name", "Fargo", "Contact", "Email", "Notes", "Extras", "Submitted"]];
    signups.forEach((s) => rows.push([
      s.promo_title || "", s.signup_type || "", s.team_name || "", s.name || "", s.fargo || "", s.phone || "", s.email || "", s.notes || "",
      Object.entries(s.extras || {}).map(([k, v]) => `${k}: ${v}`).join("; "),
      s.created_at ? new Date(s.created_at).toLocaleString() : "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `quintebca-promo-signups-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="promo-signups-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={() => setOpen((o) => !o)} data-testid="promo-signups-toggle" className="inline-flex items-center gap-2 font-heading font-bold text-lg hover:text-[#1E63C4]">
          <ChevronDown className={`h-5 w-5 text-[#999] transition-transform ${open ? "" : "-rotate-90"}`} />
          <Inbox className="h-4 w-4 text-[#1E63C4]" /> Sign-up submissions <span className="text-sm font-normal text-[#999]">({signups.length})</span>
        </button>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="promo-signups-refresh" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><RefreshCw className="h-4 w-4" /> Refresh</button>
          <button onClick={exportCsv} disabled={signups.length === 0} data-testid="promo-signups-export" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8] disabled:opacity-50"><Download className="h-4 w-4" /> Export CSV</button>
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          {signups.length === 0 ? (
            <p className="text-sm text-[#777]">No sign-ups yet. Form submissions will appear here.</p>
          ) : signups.map((s) => (
            <div key={s.id} className="border border-[#E5E7EB] rounded-md p-4" data-testid={`promo-signup-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[#111]">{s.name || "(no name)"} {s.signup_type && <span className="text-xs font-semibold text-[#1E63C4] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 ml-1">{s.signup_type}</span>} {s.team_name && <span className="text-sm font-normal text-[#999]">· {s.team_name}</span>}</p>
                  <p className="text-xs text-[#1E63C4] mt-0.5">{s.promo_title}</p>
                  <p className="text-sm text-[#555] flex flex-wrap gap-x-4 mt-1">
                    {s.fargo && <span>Fargo {s.fargo}</span>}
                    {s.phone && <span>{s.phone}</span>}
                    {s.email && <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 hover:text-[#1E63C4]"><Mail className="h-3.5 w-3.5" /> {s.email}</a>}
                  </p>
                  {Object.entries(s.extras || {}).length > 0 && (
                    <p className="text-sm text-[#555] mt-1">{Object.entries(s.extras).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
                  )}
                  {s.notes && <p className="text-sm text-[#555] mt-1 italic">{s.notes}</p>}
                  <p className="text-xs text-[#999] mt-1">Submitted {fmtDate(s.created_at)}</p>
                </div>
                <button onClick={() => del(s.id)} data-testid={`promo-signup-delete-${s.id}`} className="text-[#999] hover:text-red-500 p-1.5 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PromosManager() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [notifyModal, setNotifyModal] = useState(null); // { id }
  const [notifySubject, setNotifySubject] = useState("");
  const [notifyBody, setNotifyBody] = useState("");
  const [notifyInfo, setNotifyInfo] = useState({ count: 0, configured: true, hasImage: false });
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [includeImage, setIncludeImage] = useState(true);

  const openNotify = async (p) => {
    if (!p.id || !(p.title || "").trim()) { toast.error("Add a title first, then try again."); return; }
    setNotifyBusy(true);
    try {
      await api.put("/admin/site", site); // persist so the promo exists server-side
      const { data } = await api.get(`/admin/promos/${p.id}/notify-preview`);
      setNotifySubject(data.subject);
      setNotifyBody(data.body);
      setNotifyInfo({ count: data.count, configured: data.email_configured, hasImage: data.has_image });
      setIncludeImage(!!data.has_image);
      setNotifyModal({ id: p.id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the notification");
    } finally {
      setNotifyBusy(false);
    }
  };

  const sendNotify = async (testCopy) => {
    setNotifyBusy(true);
    try {
      const { data } = await api.post(`/admin/promos/${notifyModal.id}/notify`, { subject: notifySubject, body: notifyBody, test_copy: testCopy, include_image: includeImage });
      if (testCopy) {
        data.ok ? toast.success(data.message || "Test copy sent to the league inbox") : toast.error(data.message || "Test copy failed");
      } else if (data.ok === false) {
        toast.error(data.message || "Email isn't configured on this environment");
      } else {
        toast.success(`Sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}`);
        setNotifyModal(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setNotifyBusy(false);
    }
  };

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  if (!site) return <p className="text-[#555555]">Loading…</p>;

  const set = (k, v) => setSite((s) => ({ ...s, [k]: v }));
  const promos = site.promos || [];
  const add = () => set("promos", [{ id: crypto.randomUUID(), title: "", image: "", description: "", badge: "", cta_label: "", cta_link: "", cta_type: "link", form: { ...DEFAULT_FORM }, active: true }, ...promos]);
  const upd = (i, k, v) => set("promos", promos.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const updForm = (i, k, v) => set("promos", promos.map((p, idx) => (idx === i ? { ...p, form: { ...DEFAULT_FORM, ...(p.form || {}), [k]: v } } : p)));
  const rm = (i) => set("promos", promos.filter((_, idx) => idx !== i));

  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...promos];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    set("promos", arr);
    setDragIdx(null);
    setOverIdx(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site", site);
      toast.success("Promos saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="promos-manager">
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="save-promos-top-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save News/Promos"}
        </button>
      </div>
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <label className="block text-sm font-medium text-[#333333] mb-1">Intro text</label>
        <textarea className={input} rows={2} value={site.promos_intro || ""} onChange={(e) => set("promos_intro", e.target.value)} data-testid="promos-intro-input" />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-lg">News/Promos</h3>
          <p className="text-xs text-[#555555]">Drag the handle to reorder. Uncheck &quot;Active&quot; to hide a promo without deleting it.</p>
        </div>
        <button onClick={add} data-testid="add-promo-btn" className="inline-flex items-center gap-1 bg-[#E11D2A] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#c41320]">
          <Plus className="h-4 w-4" /> Add News/Promo
        </button>
      </div>

      <div className="space-y-3">
        {promos.map((p, i) => (
          <div
            key={p.id || i}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={() => onDrop(i)}
            data-testid={`promo-row-${i}`}
            className={`flex items-start gap-3 bg-white rounded-lg border p-4 transition-all ${
              overIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#1E63C4] ring-2 ring-[#1E63C4]/30" : "border-[#E5E7EB]"
            } ${dragIdx === i ? "opacity-50" : ""}`}
          >
            <button
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`promo-drag-${i}`}
              className="mt-1 cursor-grab active:cursor-grabbing text-[#999999] hover:text-[#1E63C4]"
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-5 w-5" />
            </button>
            <div className="flex-1 space-y-3">
              <div className="flex justify-between items-center">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={p.active !== false} onChange={(e) => upd(i, "active", e.target.checked)} className="h-4 w-4 accent-[#1E63C4]" data-testid={`promo-active-${i}`} />
                  <span className="text-xs font-semibold text-[#555555]">Active (visible on site)</span>
                </label>
                <button onClick={() => rm(i)} className="text-red-500" data-testid={`remove-promo-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <input className={input} placeholder="Title" value={p.title} onChange={(e) => upd(i, "title", e.target.value)} data-testid={`promo-title-${i}`} />
              <input className={input} placeholder="Badge (e.g. June 26–28, 2026 or Only 60 tickets)" value={p.badge} onChange={(e) => upd(i, "badge", e.target.value)} data-testid={`promo-badge-${i}`} />
              <ImageUpload label="Flyer / image" value={p.image} onChange={(v) => upd(i, "image", v)} testid={`promo-image-${i}`} />
              <div>
                <label className="block text-xs font-medium text-[#555555] mb-1">Description / details</label>
                <RichTextEditor key={p.id || i} value={p.description} onChange={(v) => upd(i, "description", v)} testid={`promo-desc-${i}`} placeholder="Description / details — type or paste formatted text" />
              </div>
              <div className="rounded-md border border-[#E5E7EB] bg-[#F9FAF8] p-3 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-[#555555] mb-1.5">Button behaviour</p>
                  <div className="flex flex-wrap gap-2">
                    <label className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm cursor-pointer ${(p.cta_type || "link") === "link" ? "border-[#1E63C4] bg-[#1E63C4]/10 text-[#1E63C4] font-semibold" : "border-[#E5E7EB] text-[#555] bg-white"}`}>
                      <input type="radio" name={`cta-type-${i}`} checked={(p.cta_type || "link") === "link"} onChange={() => upd(i, "cta_type", "link")} data-testid={`promo-ctatype-link-${i}`} className="h-3.5 w-3.5 accent-[#1E63C4]" />
                      Email / link
                    </label>
                    <label className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm cursor-pointer ${p.cta_type === "form" ? "border-[#1E63C4] bg-[#1E63C4]/10 text-[#1E63C4] font-semibold" : "border-[#E5E7EB] text-[#555] bg-white"}`}>
                      <input type="radio" name={`cta-type-${i}`} checked={p.cta_type === "form"} onChange={() => upd(i, "cta_type", "form")} data-testid={`promo-ctatype-form-${i}`} className="h-3.5 w-3.5 accent-[#1E63C4]" />
                      Pop-up sign-up form
                    </label>
                  </div>
                </div>

                {(p.cta_type || "link") === "link" ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input className={input} placeholder="Button label (e.g. Email for tickets)" value={p.cta_label} onChange={(e) => upd(i, "cta_label", e.target.value)} data-testid={`promo-cta-label-${i}`} />
                    <input className={input} placeholder="Button link (/spare-list, mailto:, tel:6077432055, or https://…)" value={p.cta_link} onChange={(e) => upd(i, "cta_link", e.target.value)} data-testid={`promo-cta-link-${i}`} />
                  </div>
                ) : (
                  <FormEditor p={p} i={i} upd={upd} updForm={updForm} />
                )}
              </div>
              <ShareToFacebook
                image={p.image}
                defaultCaption={[p.title, p.badge, p.description].filter(Boolean).join("\n\n")}
                source="promo"
                testid={`fb-promo-${i}`}
              />
              <button
                onClick={() => openNotify(p)}
                disabled={notifyBusy}
                data-testid={`promo-notify-${i}`}
                className="inline-flex items-center gap-1.5 text-sm border border-[#1E63C4] text-[#1E63C4] rounded-md px-3 py-2 font-semibold hover:bg-[#1E63C4]/10 disabled:opacity-60"
              >
                <Mail className="h-4 w-4" /> Notify subscribers…
              </button>
            </div>
          </div>
        ))}
      </div>

      {notifyModal && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => !notifyBusy && setNotifyModal(null)} data-testid="promo-notify-modal">
          <div className="bg-white rounded-xl max-w-lg w-full my-8 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="h-5 w-5 text-[#1E63C4]" />
              <h3 className="font-heading font-bold text-lg">Notify News/Promo subscribers</h3>
            </div>
            <p className="text-xs text-[#999] mb-4" data-testid="promo-notify-count">
              {notifyInfo.count} subscriber{notifyInfo.count === 1 ? "" : "s"} will receive this email.
            </p>
            {!notifyInfo.configured && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">Email isn&apos;t configured on this environment — sending will work once deployed to production.</p>
            )}
            <label className="block text-xs font-semibold text-[#555] mb-1">Subject</label>
            <input
              value={notifySubject}
              onChange={(e) => setNotifySubject(e.target.value)}
              data-testid="promo-notify-subject"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
            <label className="block text-xs font-semibold text-[#555] mb-1">Message (editable — line breaks are preserved)</label>
            <textarea
              value={notifyBody}
              onChange={(e) => setNotifyBody(e.target.value)}
              rows={10}
              data-testid="promo-notify-body"
              className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
            />
            {notifyInfo.hasImage && (
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="promo-notify-image-label">
                <input type="checkbox" checked={includeImage} onChange={(e) => setIncludeImage(e.target.checked)} data-testid="promo-notify-image" className="h-4 w-4 accent-[#1E63C4]" />
                <span className="text-[#333]">Include the flyer image at the top of the email</span>
              </label>
            )}
            <div className="flex items-center justify-end gap-2 mt-4 flex-wrap">
              <button onClick={() => setNotifyModal(null)} data-testid="promo-notify-cancel" className="text-sm border border-[#E5E7EB] rounded-md px-4 py-2 hover:bg-[#F9FAF8]">Cancel</button>
              <button onClick={() => sendNotify(true)} disabled={notifyBusy} data-testid="promo-notify-testcopy" className="inline-flex items-center gap-1.5 text-sm border border-[#1E63C4] text-[#1E63C4] rounded-md px-4 py-2 font-semibold hover:bg-[#1E63C4]/10 disabled:opacity-60">
                {notifyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send me a test copy
              </button>
              <button onClick={() => sendNotify(false)} disabled={notifyBusy || !notifyInfo.count} data-testid="promo-notify-send" className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                {notifyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to {notifyInfo.count} subscriber{notifyInfo.count === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}

      <button onClick={save} disabled={busy} data-testid="save-promos-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
        <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save News/Promos"}
      </button>

      <PromoSignups />
    </div>
  );
}
